import { useState, useEffect, useRef, useCallback, useMemo, type ReactNode } from 'react';
import { Music, X, ExternalLink, Link2, Play, Pause, Loader2, AlertTriangle, Clock, SkipForward, Sparkles, CheckCircle, Check, Edit3, ShieldCheck, Smartphone } from 'lucide-react';
import { formatDuration, SongSearchResult } from '../../services/itunesApi';
import { getSongByTrackId, searchSongs } from '../../services/songSearch';
import { resolveFromUrl, type OdesliPlatformLinks } from '../../services/odesliApi';
import { exactUrlFor, linkFitsPlayMode, modeLinkFrom, playModeConfig, searchUrlFor, type PlayMode } from '../../services/playModes';
import { SongRequest } from '../../services/requests';
import {
  QUEUED_FOR_REVIEW_MESSAGE,
  getLinkSuggestion,
  librarySongOf,
  resolveSpotifyLink,
  trySaveSongLink,
  type IntegrityStatus,
  type LibraryLink,
  type LibrarySongInput,
  type LinkProvider,
  type LinkSuggestion,
  type SaveSongLinkEntry,
} from '../../services/songMatchMemory';
import { quickInlineCheck } from '../../services/matchIntegrity';
import { canonicalLinkOrSame, cleanPastedLink, parseMusicLink } from '../../utils/songLinks';
import { attendeeDisplayName } from '../../utils/attendeeDisplayName';
import { confidenceText, isBelowThreshold } from '../../utils/linkConfidence';
import { getLinkConfidenceThreshold } from '../../services/djDefaults';
import toast from 'react-hot-toast';

export type DurationVerified = 'exact' | 'approximate' | null;

export interface SongMatchModalProps {
  /** The request being matched. null = modal closed. */
  request: SongRequest | null;
  /** Event the song belongs to, shown in the header. */
  eventName?: string;
  /** Batch progress (0/0 for single). */
  batchIndex: number;
  batchTotal: number;
  /** The event's play mode — decides the player the link must open in. */
  playMode: PlayMode;
  /** DJ's user UID for match memory lookup/recording. */
  djUid: string;
  /** Called when DJ confirms a platform link. */
  onApproveWithPlatformLink: (
    requestId: string,
    platformLink: string,
    platformName: string,
    songLinkUrl: string,
    itunesResult: SongSearchResult | null,
    durationOverrideMs: number | null,
    durationVerified: DurationVerified,
  ) => Promise<void>;
  /** Approve without platform matching (e.g. local files). */
  onApproveDirect: (requestId: string) => Promise<void>;
  /** Called when DJ rejects from modal. */
  onReject: (requestId: string, reason: string) => Promise<void>;
  /** Called when DJ defers (research later). */
  onDefer: (requestId: string) => Promise<void>;
  /** Skip to next in batch. */
  onSkip: () => void;
  /** Close / cancel. */
  onClose: () => void;
  /** Optional AI analysis handler. */
  onAnalyzeSong?: (title: string, artist: string) => void;
  /** Whether a preview device is currently connected. */
  previewSessionConnected?: boolean;
  /** Send the current song to the preview device for remote matching. */
  onSendToPreview?: (request: SongRequest) => void;
  /** URL returned from preview device — auto-fills the paste field and triggers resolution. */
  previewResultUrl?: string | null;
  /** Called after previewResultUrl is consumed so the parent can clear it. */
  onPreviewResultConsumed?: () => void;
  /** A link the DJ already pasted elsewhere (inline Link box) — pre-filled and resolved on open. */
  initialLinkUrl?: string;
  // Overrides for a host with no DJ account, the co-approval DJ page (ITER-194). Omitted = the DJ's own behavior.
  /** Saved-link lookup. Default: read the link library directly. */
  loadSuggestion?: (song: LibrarySongInput, provider: LinkProvider) => Promise<LinkSuggestion | null>;
  /** Link confidence threshold. Default: read from the DJ's defaults. */
  confidenceThreshold?: number;
  /** Spotify short-link resolver. Default: the resolveSpotifyLink callable as the DJ. */
  resolveShortLink?: (url: string) => Promise<{ trackId: string; url: string }>;
  /** Takes the library entry before onApproveWithPlatformLink runs, instead of saving it after. Default: save after approval. */
  saveLibraryEntry?: (entry: SaveSongLinkEntry) => void;
  /** Shown at the top of the modal body, e.g. a review-hold warning. */
  holdBanner?: ReactNode;
}

// Resolved match data from URL paste
interface ResolvedMatch {
  url: string;
  platform: string;
  songLinkUrl: string;
  itunesResult: SongSearchResult | null;
  title: string;       // Actual title from URL metadata (Odesli) — what goes to queue
  artist: string;      // Actual artist from URL metadata (Odesli) — what goes to queue
  hasOdesliMetadata: boolean; // Whether Odesli returned real metadata (false = fell back to iTunes search)
  albumArtUrl: string;
  odesliArtUrl: string; // Odesli thumbnail (may differ from iTunes art)
  durationMs: number;
  explicit: boolean;
  previewUrl?: string;
}

const DURATION_DISMISS_KEY = 'votebeats_duration_verify_dismissed';

const INTEGRITY_LABELS: Partial<Record<IntegrityStatus, { label: string; className: string }>> = {
  verified: { label: 'Verified', className: 'text-green-600 dark:text-green-400' },
  matched: { label: 'Checked', className: 'text-green-600 dark:text-green-400' },
  mismatch: { label: 'Possible mismatch', className: 'text-amber-600 dark:text-amber-400' },
  error: { label: 'Check failed', className: 'text-gray-500' },
};

const SUGGESTION_WARNINGS: Record<Exclude<LinkSuggestion['match'], 'exact'>, string> = {
  isrc: 'Same recording, saved for a different release — check it before using',
  title: 'Matched by title only — check it before using',
};

function savedDateOf(link: LibraryLink): string {
  const date = new Date(link.setAt);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
}

function parseMmSs(input: string): number | null {
  const match = input.trim().match(/^(\d{1,3}):(\d{2})$/);
  if (!match) return null;
  const mins = parseInt(match[1], 10);
  const secs = parseInt(match[2], 10);
  if (secs >= 60) return null;
  return (mins * 60 + secs) * 1000;
}

function formatMmSs(ms: number): string {
  if (!ms) return '';
  const totalSecs = Math.floor(ms / 1000);
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export default function SongMatchModal({
  request,
  eventName,
  batchIndex,
  batchTotal,
  playMode,
  djUid,
  onApproveWithPlatformLink,
  onApproveDirect,
  onReject,
  onDefer,
  onSkip,
  onClose,
  onAnalyzeSong,
  previewSessionConnected,
  onSendToPreview,
  previewResultUrl,
  onPreviewResultConsumed,
  initialLinkUrl,
  loadSuggestion,
  confidenceThreshold: confidenceThresholdOverride,
  resolveShortLink,
  saveLibraryEntry,
  holdBanner,
}: SongMatchModalProps) {
  const [matchPasteUrl, setMatchPasteUrl] = useState('');
  const [matchPasteLoading, setMatchPasteLoading] = useState(false);
  const mode = playModeConfig(playMode);
  const platformLabel = mode.playerName;
  const [matchPreviewUrl, setMatchPreviewUrl] = useState<string | null>(null);
  const matchPreviewAudioRef = useRef<HTMLAudioElement | null>(null);
  const pasteInputRef = useRef<HTMLInputElement | null>(null);
  const [originalPreviewUrl, setOriginalPreviewUrl] = useState<string | null>(null);
  const [originalPreviewLoading, setOriginalPreviewLoading] = useState(false);
  const [resolvedMatch, setResolvedMatch] = useState<ResolvedMatch | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  // Saved link from the shared library, keyed by the song as the DJ opened it (ITER-122)
  const originalSong = useMemo(
    () => request ? librarySongOf(request.song) : null,
    [request?.id], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const [suggestion, setSuggestion] = useState<LinkSuggestion | null>(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [usedSuggestion, setUsedSuggestion] = useState(false);
  const [confidenceThreshold, setConfidenceThreshold] = useState(2);
  // Match notes
  const [matchNotes, setMatchNotes] = useState('');
  // Title/artist/artwork override — lets DJ correct bad metadata from URL resolution
  const [titleOverride, setTitleOverride] = useState('');
  const [artistOverride, setArtistOverride] = useState('');
  const [artworkOverride, setArtworkOverride] = useState('');
  const [editingMeta, setEditingMeta] = useState(false);
  const [markVerified, setMarkVerified] = useState(false);
  // Duration verification
  const [durationInput, setDurationInput] = useState('');
  const [durationPreFilled, setDurationPreFilled] = useState(false);
  const [durationDismissedForEvent, setDurationDismissedForEvent] = useState(() =>
    sessionStorage.getItem(DURATION_DISMISS_KEY) === 'true'
  );
  // Preview device "Sent!" confirmation
  const [previewSent, setPreviewSent] = useState(false);

  useEffect(() => {
    if (!request) return;
    if (confidenceThresholdOverride !== undefined) {
      setConfidenceThreshold(confidenceThresholdOverride);
      return;
    }
    if (!djUid) return;
    getLinkConfidenceThreshold(djUid).then(setConfidenceThreshold);
  }, [request?.id, djUid, confidenceThresholdOverride]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!request) return;
    setMatchPasteUrl('');
    setResolvedMatch(null);
    setSuggestion(null);
    setUsedSuggestion(false);
    setMatchNotes('');
    setTitleOverride('');
    setArtistOverride('');
    setArtworkOverride('');
    setEditingMeta(false);
    setMarkVerified(false);
    // Look up the saved link for this song on the event's player
    setSuggestionsLoading(true);
    (loadSuggestion ?? getLinkSuggestion)(originalSong!, mode.provider)
      .then(setSuggestion)
      .catch(err => console.warn('[SongMatchModal] Saved link lookup failed:', err))
      .finally(() => setSuggestionsLoading(false));
    // Pre-fill duration from existing request data
    if (request.song.durationMs) {
      setDurationInput(formatMmSs(request.song.durationMs));
      setDurationPreFilled(true);
    } else {
      setDurationInput('');
      setDurationPreFilled(false);
    }
    // A link the DJ pasted in the inline Link box goes through the same resolve path as a paste here
    if (initialLinkUrl) {
      setMatchPasteUrl(initialLinkUrl);
      pendingResolveRef.current = true;
    } else if (request.platformLink) {
      // If request already has a platformLink (URL-submitted), auto-resolve it
      setMatchPasteUrl(request.platformLink);
      // Auto-trigger lookup for the existing URL
      (async () => {
        const platform = request.platformName || 'Unknown';
        let songLinkUrl = request.songLinkUrl || '';
        let itunesResult: SongSearchResult | null = null;
        let odesliTitle = '';
        let odesliArtist = '';
        let odesliArt = '';
        let resolvedLinks: OdesliPlatformLinks | null = null;
        try {
          const resolved = await resolveFromUrl(request.platformLink!);
          if (resolved) {
            resolvedLinks = resolved.platformLinks;
            if (!songLinkUrl) songLinkUrl = resolved.songLinkUrl;
            if (resolved.metadata) {
              odesliTitle = resolved.metadata.title;
              odesliArtist = resolved.metadata.artistName;
              odesliArt = resolved.metadata.thumbnailUrl || '';
            }
            if (resolved.metadata?.apiProvider === 'itunes' && resolved.metadata.id) {
              itunesResult = await getSongByTrackId(Number(resolved.metadata.id), mode.searchCatalog).catch(() => null);
            } else if (resolved.platformLinks.appleMusic) {
              const idMatch = resolved.platformLinks.appleMusic.match(/\/(\d{5,})/);
              if (idMatch) itunesResult = await getSongByTrackId(Number(idMatch[1]), mode.searchCatalog).catch(() => null);
            }
            if (!itunesResult && resolved.metadata) {
              itunesResult = {
                trackId: 0, trackName: resolved.metadata.title, artistName: resolved.metadata.artistName,
                collectionName: '', artworkUrl60: resolved.metadata.thumbnailUrl || '',
                artworkUrl100: resolved.metadata.thumbnailUrl || '', trackTimeMillis: 0,
                trackExplicitness: 'notExplicit' as const,
                previewUrl: '',
                isrc: null,
              };
            }
          }
        } catch { /* non-critical */ }
        // If no real iTunes result or no duration, search iTunes by title+artist
        if (!itunesResult || !itunesResult.trackTimeMillis) {
          try {
            const searchTitle = itunesResult?.trackName || request.song.title;
            const searchArtist = itunesResult?.artistName || request.song.artist;
            const results = await searchSongs(`${searchTitle} ${searchArtist}`, 5, 'song', mode.searchCatalog);
            // Pick the best match by title+artist similarity
            const match = results.find(r =>
              r.trackTimeMillis > 0 && (
                r.trackName.toLowerCase().includes(searchTitle.toLowerCase()) ||
                searchTitle.toLowerCase().includes(r.trackName.toLowerCase())
              )
            ) || results.find(r => r.trackTimeMillis > 0) || results[0];
            if (match && match.trackTimeMillis > 0) {
              itunesResult = match;
            }
          } catch { /* non-critical */ }
        }
        const fitted = fitToMode(request.platformLink!, platform, resolvedLinks);
        if (!fitted) return;
        setResolvedMatch({
          url: fitted.url,
          platform: fitted.platform,
          songLinkUrl,
          itunesResult,
          // Odesli metadata is source of truth (what the URL actually points to)
          title: odesliTitle || itunesResult?.trackName || request.song.title,
          artist: odesliArtist || itunesResult?.artistName || request.song.artist,
          hasOdesliMetadata: !!odesliTitle,
          albumArtUrl: itunesResult?.artworkUrl100 || odesliArt || request.song.albumArtUrl || '',
          odesliArtUrl: odesliArt,
          durationMs: itunesResult?.trackTimeMillis || request.song.durationMs || 0,
          explicit: itunesResult?.trackExplicitness === 'explicit' || request.song.explicitFlag || false,
          previewUrl: itunesResult?.previewUrl,
        });
      })();
    }
    if (request.song.previewUrl) {
      setOriginalPreviewUrl(request.song.previewUrl);
      setOriginalPreviewLoading(false);
    } else if (request.song.itunesTrackId) {
      setOriginalPreviewUrl(null);
      setOriginalPreviewLoading(true);
      getSongByTrackId(Number(request.song.itunesTrackId), mode.searchCatalog)
        .then(song => { setOriginalPreviewUrl(song?.previewUrl || null); setOriginalPreviewLoading(false); })
        .catch(() => setOriginalPreviewLoading(false));
    } else {
      setOriginalPreviewUrl(null);
      setOriginalPreviewLoading(false);
    }
  }, [request?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fill duration input when resolved match changes
  useEffect(() => {
    if (resolvedMatch?.durationMs) {
      setDurationInput(formatMmSs(resolvedMatch.durationMs));
      setDurationPreFilled(true);
    } else {
      setDurationInput('');
      setDurationPreFilled(false);
    }
  }, [resolvedMatch?.durationMs]);

  useEffect(() => {
    return () => { matchPreviewAudioRef.current?.pause(); };
  }, []);

  const fitToMode = useCallback((url: string, platform: string, links: OdesliPlatformLinks | null): { url: string; platform: string } | null => {
    if (linkFitsPlayMode(url, platform, mode)) return { url: canonicalLinkOrSame(url), platform };
    const converted = modeLinkFrom(links, mode);
    return converted ? { url: canonicalLinkOrSame(converted), platform: mode.playerName } : null;
  }, [mode]);

  // Step 1: Resolve URL
  const handleResolveUrl = useCallback(async () => {
    const pasted = matchPasteUrl.trim();
    if (!pasted || !request) return;
    setMatchPasteLoading(true);
    setResolvedMatch(null);
    try {
      const cleaned = await cleanPastedLink(pasted, resolveShortLink ?? resolveSpotifyLink);
      if (!cleaned.ok) {
        toast.error(cleaned.message);
        return;
      }
      const url = cleaned.url;
      const platform = cleaned.platformName;

      let songLinkUrl = '';
      let itunesResult: SongSearchResult | null = null;
      let odesliTitle = '';
      let odesliArtist = '';
      let odesliArt = '';
      let resolvedLinks: OdesliPlatformLinks | null = null;

      try {
        const resolved = await resolveFromUrl(url);
        if (resolved) {
          resolvedLinks = resolved.platformLinks;
          songLinkUrl = resolved.songLinkUrl;
          if (resolved.metadata) {
            odesliTitle = resolved.metadata.title;
            odesliArtist = resolved.metadata.artistName;
            odesliArt = resolved.metadata.thumbnailUrl || '';
          }
          if (resolved.metadata?.apiProvider === 'itunes' && resolved.metadata.id) {
            try { itunesResult = await getSongByTrackId(Number(resolved.metadata.id), mode.searchCatalog); } catch { /* */ }
          } else if (resolved.platformLinks.appleMusic) {
            const idMatch = resolved.platformLinks.appleMusic.match(/\/(\d{5,})/);
            if (idMatch) {
              try { itunesResult = await getSongByTrackId(Number(idMatch[1]), mode.searchCatalog); } catch { /* */ }
            }
          }
          if (!itunesResult && resolved.metadata) {
            itunesResult = {
              trackId: resolved.metadata.id ? Number(resolved.metadata.id) : 0,
              trackName: resolved.metadata.title,
              artistName: resolved.metadata.artistName,
              collectionName: '',
              artworkUrl60: resolved.metadata.thumbnailUrl || '',
              artworkUrl100: resolved.metadata.thumbnailUrl || '',
              trackTimeMillis: 0,
              trackExplicitness: 'notExplicit' as const,
              previewUrl: '',
              isrc: null,
            };
          }
        }
      } catch {
        console.warn('[SongMatchModal] Odesli resolution failed for:', url);
      }

      const fitted = fitToMode(url, platform, resolvedLinks);
      if (!fitted) {
        toast.error(`That's a ${platform} link. This event plays ${mode.playerName}.`);
        return;
      }

      // If no real iTunes result or no duration, search iTunes by title+artist
      if (!itunesResult || !itunesResult.trackTimeMillis) {
        try {
          const searchTitle = odesliTitle || request.song.title;
          const searchArtist = odesliArtist || request.song.artist;
          const results = await searchSongs(`${searchTitle} ${searchArtist}`, 5, 'song', mode.searchCatalog);
          const match = results.find(r =>
            r.trackTimeMillis > 0 && (
              r.trackName.toLowerCase().includes(searchTitle.toLowerCase()) ||
              searchTitle.toLowerCase().includes(r.trackName.toLowerCase())
            )
          ) || results.find(r => r.trackTimeMillis > 0) || results[0];
          if (match && match.trackTimeMillis > 0) {
            itunesResult = match;
          }
        } catch { /* non-critical */ }
      }

      setResolvedMatch({
        url: fitted.url,
        platform: fitted.platform,
        songLinkUrl,
        itunesResult,
        // Odesli metadata is source of truth (what the URL actually points to)
        // Falls back to iTunes only when Odesli returned nothing
        title: odesliTitle || itunesResult?.trackName || '(Unknown title)',
        artist: odesliArtist || itunesResult?.artistName || '(Unknown artist)',
        hasOdesliMetadata: !!odesliTitle,
        albumArtUrl: itunesResult?.artworkUrl100 || odesliArt || '',
        odesliArtUrl: odesliArt,
        durationMs: itunesResult?.trackTimeMillis || 0,
        explicit: itunesResult?.trackExplicitness === 'explicit',
        previewUrl: itunesResult?.previewUrl,
      });
    } catch (err) {
      console.error('[SongMatchModal] Link lookup failed:', err);
      toast.error('Could not look up that link — please try again');
    } finally {
      setMatchPasteLoading(false);
    }
  }, [matchPasteUrl, request, fitToMode, mode, resolveShortLink]);

  // Effective title/artist/artwork — DJ overrides take precedence over resolved metadata
  const effectiveTitle = (titleOverride.trim() || resolvedMatch?.title) ?? '';
  const effectiveArtist = (artistOverride.trim() || resolvedMatch?.artist) ?? '';
  const effectiveArtwork = artworkOverride || resolvedMatch?.albumArtUrl || '';

  // Inline integrity check — compare requested song against actual URL metadata (Odesli)
  // Only runs when Odesli returned real metadata; skipped if fell back to iTunes search
  const inlineMismatch = useMemo(() => {
    if (!resolvedMatch || !request || !resolvedMatch.hasOdesliMetadata) return null;
    return quickInlineCheck(
      request.song.title, request.song.artist,
      resolvedMatch.title, resolvedMatch.artist,
    );
  }, [resolvedMatch, request]);

  // Step 2: Confirm with duration verification
  const handleConfirm = useCallback(async (durationVerified: DurationVerified) => {
    if (!request || !resolvedMatch) return;
    setConfirmLoading(true);
    try {
      let durationOverrideMs: number | null = null;

      if (usedSuggestion && suggestionDurationRef.current?.ms) {
        // Using saved match — use the previously verified duration
        durationOverrideMs = suggestionDurationRef.current.ms;
      } else if (durationVerified === 'exact') {
        // DJ entered a custom duration
        const parsed = parseMmSs(durationInput);
        if (parsed !== null && parsed > 0) {
          durationOverrideMs = parsed;
        }
      }
      // 'approximate' = use iTunes duration as-is (no override)
      // null = dismissed, no verification

      // Build the itunesResult to pass downstream — use effective title/artist (DJ overrides if set)
      // so the queue stores the correct data (critical for Desktop Helper matching)
      const itunesForApproval: SongSearchResult | null = resolvedMatch.itunesResult
        ? {
            ...resolvedMatch.itunesResult,
            trackName: effectiveTitle,
            artistName: effectiveArtist,
            artworkUrl60: effectiveArtwork,
            artworkUrl100: effectiveArtwork,
          }
        : effectiveTitle !== '(Unknown title)'
          ? {
              trackId: 0,
              trackName: effectiveTitle,
              artistName: effectiveArtist,
              collectionName: '',
              artworkUrl60: effectiveArtwork,
              artworkUrl100: effectiveArtwork,
              trackTimeMillis: durationOverrideMs || resolvedMatch.durationMs || 0,
              trackExplicitness: resolvedMatch.explicit ? 'explicit' : 'notExplicit',
              previewUrl: '',
              isrc: null,
            }
          : null;

      // A used saved link brings its own song.link page when Odesli returned none for the same track
      const resolvedParsed = parseMusicLink(resolvedMatch.url);
      const savedSongLinkUrl = usedSuggestion && suggestion
        && resolvedParsed.kind === 'track' && resolvedParsed.trackId === suggestion.link.trackId
        ? suggestion.link.songLinkUrl
        : null;
      const songLinkUrl = resolvedMatch.songLinkUrl || savedSongLinkUrl || '';

      // Save to the shared library under the song as the DJ opened it (fire-and-forget, never blocks approval).
      // Using an exact saved link only adds this DJ; a fallback suggestion is saved under this song's own key.
      const usedExactLink = usedSuggestion && suggestion?.match === 'exact';
      const integrityStatus: IntegrityStatus = markVerified
        ? 'verified'
        : inlineMismatch
          ? (inlineMismatch.isMismatch ? 'mismatch' : 'matched')
          : 'unchecked';
      const entry: SaveSongLinkEntry = usedExactLink
        ? { song: originalSong!, link: resolvedMatch.url, eventId: request.eventId, requestId: request.id }
        : {
            song: originalSong!,
            link: resolvedMatch.url,
            songLinkUrl: songLinkUrl || null,
            albumArtUrl: effectiveArtwork || null,
            durationMs: durationOverrideMs || resolvedMatch.durationMs || null,
            durationVerified,
            note: matchNotes.trim() || undefined,
            eventId: request.eventId,
            requestId: request.id,
            ...(usedSuggestion ? {} : {
              integrity: {
                integrityStatus,
                resolvedTitle: effectiveTitle || null,
                resolvedArtist: effectiveArtist || null,
                titleSimilarity: inlineMismatch?.titleSim ?? null,
                artistSimilarity: inlineMismatch?.artistSim ?? null,
                lastCheckedAt: new Date().toISOString(),
              },
            }),
          };
      // A host without a DJ account sends the entry along with its approval instead (ITER-194)
      saveLibraryEntry?.(entry);
      await onApproveWithPlatformLink(
        request.id,
        resolvedMatch.url,
        resolvedMatch.platform,
        songLinkUrl,
        itunesForApproval,
        durationOverrideMs,
        durationVerified,
      );
      if (saveLibraryEntry) return;
      trySaveSongLink(entry).then(outcome => {
        if (outcome.error) toast.error(`Link approved, but saving it to the library failed: ${outcome.error}`);
        else if (outcome.queuedForReview) toast(QUEUED_FOR_REVIEW_MESSAGE, { duration: 6000 });
      });
    } finally {
      setConfirmLoading(false);
    }
  }, [request, resolvedMatch, durationInput, onApproveWithPlatformLink, originalSong, suggestion, usedSuggestion, inlineMismatch, effectiveTitle, effectiveArtist, effectiveArtwork, matchNotes, markVerified, saveLibraryEntry]);

  const handleDismissOnce = useCallback(async () => {
    await handleConfirm(null);
  }, [handleConfirm]);

  const handleDismissForEvent = useCallback(async () => {
    sessionStorage.setItem(DURATION_DISMISS_KEY, 'true');
    setDurationDismissedForEvent(true);
    await handleConfirm(null);
  }, [handleConfirm]);

  const handleMatchPreview = useCallback((previewUrl: string) => {
    if (matchPreviewUrl === previewUrl) {
      matchPreviewAudioRef.current?.pause();
      setMatchPreviewUrl(null);
    } else {
      matchPreviewAudioRef.current?.pause();
      const audio = new Audio(previewUrl);
      audio.volume = 0.5;
      audio.onended = () => setMatchPreviewUrl(null);
      audio.play();
      matchPreviewAudioRef.current = audio;
      setMatchPreviewUrl(previewUrl);
    }
  }, [matchPreviewUrl]);

  // Use a match suggestion: auto-fill URL and trigger resolution
  const pendingResolveRef = useRef(false);
  const suggestionDurationRef = useRef<{ ms: number | null; verified: DurationVerified } | null>(null);
  const handleUseSuggestion = useCallback((link: LibraryLink) => {
    setMatchPasteUrl(link.url);
    setResolvedMatch(null);
    setUsedSuggestion(true);
    // Preserve duration from saved match so we can skip duration verification
    if (link.durationMs) {
      suggestionDurationRef.current = { ms: link.durationMs, verified: link.durationVerified || 'approximate' };
    } else {
      suggestionDurationRef.current = null;
    }
    pendingResolveRef.current = true;
  }, []);

  // Auto-resolve when URL is set from a suggestion
  useEffect(() => {
    if (pendingResolveRef.current && matchPasteUrl) {
      pendingResolveRef.current = false;
      handleResolveUrl();
    }
  }, [matchPasteUrl, handleResolveUrl]);

  // Auto-fill and resolve when preview device returns a URL
  useEffect(() => {
    if (previewResultUrl && request) {
      setMatchPasteUrl(previewResultUrl);
      setResolvedMatch(null);
      pendingResolveRef.current = true;
      onPreviewResultConsumed?.();
    }
  }, [previewResultUrl, request, onPreviewResultConsumed]);

  if (!request) return null;

  const suggestionNeverCache = suggestion?.song.linkStats[suggestion.provider]?.neverCache === true;

  const resolvedTrackId = (() => {
    const parsed = resolvedMatch ? parseMusicLink(resolvedMatch.url) : null;
    return parsed?.kind === 'track' ? parsed.trackId : null;
  })();
  const replacesSavedLink = Boolean(
    suggestion?.match === 'exact' && resolvedTrackId && resolvedTrackId !== suggestion.link.trackId,
  );
  const durationInputValid = !durationInput.trim() || parseMmSs(durationInput) !== null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Music className="w-5 h-5 text-primary flex-shrink-0" /> Match Song for Playback
            </h3>
            {(eventName || batchTotal > 0) && (
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5" title={eventName}>
                {batchTotal > 0 && `${batchIndex} of ${batchTotal}`}
                {eventName && batchTotal > 0 && ' · '}
                {eventName}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg flex-shrink-0"><X className="w-5 h-5" /></button>
        </div>

        {holdBanner}

        {/* Requested song info */}
        <div className={`flex items-center gap-3 p-3 rounded-lg mb-4 ${request.song.explicitFlag ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' : 'bg-gray-50 dark:bg-gray-800/50'}`}>
          {request.song.albumArtUrl ? (
            <img src={request.song.albumArtUrl} alt="" className="w-12 h-12 rounded flex-shrink-0 object-cover" />
          ) : (
            <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded flex-shrink-0 flex items-center justify-center"><Music className="w-6 h-6 text-gray-400" /></div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900 dark:text-white text-sm truncate flex items-center gap-1.5">
              <span className="truncate">{request.song.title}</span>
              {request.song.durationMs ? (
                <span className="text-xs text-gray-400 flex-shrink-0">{formatDuration(request.song.durationMs)}</span>
              ) : null}
              {request.song.explicitFlag && <span className="explicit-badge flex-shrink-0 text-xs">E</span>}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400 truncate">{request.song.artist}</p>
            <div className="flex items-center gap-2 flex-wrap">
              {request.song.itunesTrackId && (
                <a href={`https://music.apple.com/us/song/${request.song.itunesTrackId}`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-pink-500 hover:text-pink-400 transition-colors" onClick={(e) => e.stopPropagation()}>
                  <ExternalLink className="w-3 h-3" /> Apple Music
                </a>
              )}
              {originalPreviewLoading ? (
                <span className="inline-flex items-center gap-1 text-xs text-gray-400"><Loader2 className="w-3 h-3 animate-spin" /> Loading...</span>
              ) : originalPreviewUrl ? (
                <button onClick={(e) => { e.stopPropagation(); handleMatchPreview(originalPreviewUrl); }}
                  className={`inline-flex items-center gap-1 text-xs font-medium transition-colors ${matchPreviewUrl === originalPreviewUrl ? 'text-pink-600 dark:text-pink-400' : 'text-pink-500 hover:text-pink-400'}`}
                  title={matchPreviewUrl === originalPreviewUrl ? 'Stop preview' : 'Preview original (30s)'}>
                  {matchPreviewUrl === originalPreviewUrl ? <><Pause className="w-3 h-3" /> Playing...</> : <><Play className="w-3 h-3" /> Preview</>}
                </button>
              ) : null}
            </div>
            <p className="text-xs text-gray-500">by {attendeeDisplayName(request.requestedBy.nickname, request.requestedBy.oderId)}</p>
          </div>
          {request.song.explicitFlag && (
            <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-xs font-bold uppercase tracking-wide">Explicit</span>
            </div>
          )}
          {onAnalyzeSong && (
            <button onClick={() => onAnalyzeSong(request.song.title, request.song.artist)}
              className="p-1.5 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 text-purple-400 hover:text-purple-500 flex-shrink-0" title="AI Song Analysis">
              <Sparkles className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Send to Preview Device */}
        {previewSessionConnected && onSendToPreview && (
          <button
            onClick={() => {
              onSendToPreview(request);
              setPreviewSent(true);
              setTimeout(() => setPreviewSent(false), 2000);
            }}
            className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 bg-gray-100 dark:bg-gray-800 px-2.5 py-1.5 rounded-lg transition-colors mb-3"
          >
            <Smartphone className="w-3.5 h-3.5" />
            {previewSent ? 'Sent!' : 'Send to Preview Device'}
          </button>
        )}

        {/* Saved link from the shared library */}
        {suggestionsLoading && (
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking the link library...
          </div>
        )}
        {suggestion && !usedSuggestion && (
          <div className="mb-4 p-4 rounded-lg border-2 border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 shadow-sm">
            <p className="text-sm font-semibold text-green-700 dark:text-green-400 mb-3 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /> Saved Link
            </p>
            {suggestion.match !== 'exact' && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mb-2 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> {SUGGESTION_WARNINGS[suggestion.match]}
              </p>
            )}
            <div className="flex items-center gap-3">
              {suggestion.link.albumArtUrl ? (
                <img src={suggestion.link.albumArtUrl} alt="" className="w-12 h-12 rounded flex-shrink-0 object-cover" />
              ) : (
                <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded flex-shrink-0 flex items-center justify-center"><Music className="w-6 h-6 text-gray-400" /></div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate flex items-center gap-1.5">
                  <span className="truncate">{suggestion.link.platformName}</span>
                  {suggestion.link.durationMs ? (
                    <span className="text-xs text-gray-500 flex-shrink-0">{formatDuration(suggestion.link.durationMs)}</span>
                  ) : null}
                  {suggestion.link.durationMs && suggestion.link.durationVerified === 'exact' && (
                    <span title="Duration verified"><ShieldCheck className="w-3.5 h-3.5 text-green-500 flex-shrink-0" /></span>
                  )}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {suggestion.link.setBy === djUid ? 'Saved by you' : 'Saved by another DJ'}
                  {savedDateOf(suggestion.link) ? ` · ${savedDateOf(suggestion.link)}` : ''}
                  {suggestion.link.integrityStatus && INTEGRITY_LABELS[suggestion.link.integrityStatus] && (
                    <span className={INTEGRITY_LABELS[suggestion.link.integrityStatus]!.className}>
                      {' · '}{INTEGRITY_LABELS[suggestion.link.integrityStatus]!.label}
                    </span>
                  )}
                </p>
                {suggestion.link.note && (
                  <p className="text-xs text-gray-500 italic truncate">{suggestion.link.note}</p>
                )}
                <p className="text-xs text-gray-500">{confidenceText(suggestion.link)}</p>
                {isBelowThreshold(suggestion.link, confidenceThreshold) && !suggestionNeverCache && (
                  <>
                    <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                      Fewer than {confidenceThreshold} clean uses — double-check before using.
                    </p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                      <button
                        onClick={() => { window.location.href = exactUrlFor(suggestion.link.trackId, mode); }}
                        className="inline-flex items-center gap-1 font-medium text-green-700 dark:text-green-400 hover:underline"
                      >
                        <ExternalLink className="w-3 h-3" /> Open in {platformLabel}
                      </button>
                      <button
                        onClick={() => { pasteInputRef.current?.focus(); pasteInputRef.current?.scrollIntoView({ block: 'center' }); }}
                        className="font-medium text-amber-700 dark:text-amber-400 hover:underline"
                      >
                        Not right? Paste the correct link
                      </button>
                    </div>
                  </>
                )}
              </div>
              {!suggestionNeverCache && (
                <button
                  onClick={() => handleUseSuggestion(suggestion.link)}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium flex-shrink-0 flex items-center gap-1.5"
                >
                  <Check className="w-4 h-4" /> Use
                </button>
              )}
            </div>
            {suggestionNeverCache && (
              <p className="mt-2 text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                This song's link keeps changing, so it isn't reused. Search for the song instead.
              </p>
            )}
          </div>
        )}

        {/* Search in player button */}
        <button
          onClick={() => {
            const url = searchUrlFor(request.song.title, request.song.artist, mode);
            if (url.startsWith('http://') || url.startsWith('https://')) {
              window.open(url, '_blank', 'noopener');
            } else {
              window.location.href = url;
            }
          }}
          className="w-full flex items-center justify-center gap-2 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium text-sm mb-4"
        >
          <ExternalLink className="w-4 h-4" />
          Search in {platformLabel}
        </button>

        {/* Paste URL */}
        <div className="flex gap-2 mb-1">
          <div className="relative flex-1">
            <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input ref={pasteInputRef} type="text" value={matchPasteUrl} onChange={(e) => { setMatchPasteUrl(e.target.value); setResolvedMatch(null); }}
              onKeyDown={(e) => e.key === 'Enter' && handleResolveUrl()}
              placeholder={`Paste ${platformLabel} track URL...`}
              className={`input pl-9 w-full text-sm ${matchPasteUrl ? 'pr-8' : ''}`} autoFocus />
            {matchPasteUrl && (
              <button
                onClick={() => { setMatchPasteUrl(''); setResolvedMatch(null); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
                title="Clear URL"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <button onClick={handleResolveUrl} disabled={matchPasteLoading || !matchPasteUrl.trim()} className="btn btn-primary text-sm">
            {matchPasteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Look Up'}
          </button>
        </div>
        <p className="text-xs text-gray-400 mb-3">
          Find the exact song in {platformLabel}, copy its share link, and paste above.
        </p>

        {/* Resolved match preview */}
        {resolvedMatch && (
          <div className="mb-4 p-3 rounded-lg border-2 border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20">
            <p className="text-xs font-medium text-green-700 dark:text-green-400 mb-2">Matched from {resolvedMatch.platform}:</p>
            <div className="flex items-center gap-3 mb-3">
              {effectiveArtwork ? (
                <img src={effectiveArtwork} alt="" className="w-12 h-12 rounded flex-shrink-0 object-cover" />
              ) : (
                <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded flex-shrink-0 flex items-center justify-center"><Music className="w-6 h-6 text-gray-400" /></div>
              )}
              <div className="flex-1 min-w-0">
                {editingMeta ? (
                  <div className="space-y-1">
                    <input type="text" value={titleOverride || resolvedMatch.title}
                      onChange={e => setTitleOverride(e.target.value)}
                      className="input text-sm w-full py-0.5 px-1.5 font-medium" placeholder="Title" />
                    <input type="text" value={artistOverride || resolvedMatch.artist}
                      onChange={e => setArtistOverride(e.target.value)}
                      className="input text-xs w-full py-0.5 px-1.5" placeholder="Artist" />
                    {/* Artwork picker — show available options */}
                    {(() => {
                      const artOptions: { url: string; label: string }[] = [];
                      if (resolvedMatch.itunesResult?.artworkUrl100) artOptions.push({ url: resolvedMatch.itunesResult.artworkUrl100, label: 'Catalog' });
                      if (resolvedMatch.odesliArtUrl && !artOptions.some(a => a.url === resolvedMatch.odesliArtUrl)) artOptions.push({ url: resolvedMatch.odesliArtUrl, label: resolvedMatch.platform });
                      if (request.song.albumArtUrl && !artOptions.some(a => a.url === request.song.albumArtUrl)) artOptions.push({ url: request.song.albumArtUrl, label: 'Original' });
                      if (artOptions.length <= 1) return null;
                      return (
                        <div className="mt-1.5">
                          <p className="text-xs text-gray-500 mb-1">Artwork:</p>
                          <div className="flex gap-2">
                            {artOptions.map(opt => (
                              <button key={opt.url} type="button" onClick={() => setArtworkOverride(opt.url)}
                                className={`relative rounded overflow-hidden flex-shrink-0 ${effectiveArtwork === opt.url ? 'ring-2 ring-green-500' : 'ring-1 ring-gray-300 dark:ring-gray-600 opacity-70 hover:opacity-100'}`}
                                title={opt.label}>
                                <img src={opt.url} alt={opt.label} className="w-10 h-10 object-cover" />
                                <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[9px] text-center leading-tight py-px">{opt.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <>
                    <p className="font-medium text-gray-900 dark:text-white text-sm truncate flex items-center gap-1.5">
                      <span className="truncate">{effectiveTitle}</span>
                      {resolvedMatch.explicit && <span className="explicit-badge flex-shrink-0 text-xs">E</span>}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 truncate">{effectiveArtist}</p>
                  </>
                )}
              </div>
              <button onClick={() => {
                if (!editingMeta) {
                  setTitleOverride(titleOverride || resolvedMatch.title);
                  setArtistOverride(artistOverride || resolvedMatch.artist);
                }
                setEditingMeta(!editingMeta);
              }}
                className="p-1 rounded hover:bg-green-100 dark:hover:bg-green-800/50 text-gray-400 hover:text-gray-600 flex-shrink-0"
                title={editingMeta ? 'Done editing' : 'Edit title/artist/artwork'}>
                <Edit3 className="w-3.5 h-3.5" />
              </button>
              {resolvedMatch.previewUrl && (
                <button onClick={() => handleMatchPreview(resolvedMatch.previewUrl!)}
                  className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${matchPreviewUrl === resolvedMatch.previewUrl ? 'bg-green-200 dark:bg-green-800 text-green-700' : 'hover:bg-green-100 dark:hover:bg-green-800/50 text-gray-400'}`}
                  title={matchPreviewUrl === resolvedMatch.previewUrl ? 'Stop preview' : 'Preview matched song'}>
                  {matchPreviewUrl === resolvedMatch.previewUrl ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                </button>
              )}
            </div>

            {/* Inline integrity mismatch warning */}
            {inlineMismatch?.isMismatch && (
              <div className="mb-3 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700">
                <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-1.5 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> URL doesn't match the requested song
                </p>
                <div className="text-xs space-y-0.5 text-amber-600 dark:text-amber-300">
                  <p>Requested: <span className="font-medium">"{request.song.title}"</span> by {request.song.artist}</p>
                  <p>URL resolves to: <span className="font-medium">"{resolvedMatch.title}"</span>{resolvedMatch.artist ? ` by ${resolvedMatch.artist}` : ''}</p>
                </div>
                <p className="text-xs text-amber-500 dark:text-amber-400 mt-1">
                  Title: {Math.round(inlineMismatch.titleSim * 100)}%{inlineMismatch.artistSim < 1 ? <> &middot; Artist: {Math.round(inlineMismatch.artistSim * 100)}%</> : ''}
                </p>
                <p className="text-xs text-amber-500/70 mt-1">The queue will show the URL's title/artist so Bridge can recognize it.</p>
              </div>
            )}
            {/* Warning when Odesli metadata unavailable — integrity check couldn't run */}
            {resolvedMatch && !resolvedMatch.hasOdesliMetadata && !inlineMismatch && (
              <div className="mb-3 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700">
                <p className="text-xs font-medium text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> Could not verify URL — metadata unavailable
                </p>
                <p className="text-xs text-amber-500/80 mt-1">
                  The URL couldn't be verified against the requested song (Odesli may be rate-limited). Double-check that this link plays the correct song before confirming.
                </p>
              </div>
            )}

            {/* Match notes — optional, encouraged for different versions */}
            {!usedSuggestion && (
              <div className="mb-3">
                <input
                  type="text"
                  value={matchNotes}
                  onChange={e => setMatchNotes(e.target.value)}
                  placeholder="Add a note (e.g., 'clean version', 'radio edit', 'acoustic')"
                  className="input w-full text-xs"
                  maxLength={100}
                />
                {replacesSavedLink && (
                  <p className="text-xs text-amber-500 mt-1">
                    A different link is saved for this song — confirming replaces it for every DJ.
                  </p>
                )}
              </div>
            )}

            {/* Mark as verified checkbox — show when DJ edited metadata or mismatch detected */}
            {!usedSuggestion && (editingMeta || titleOverride || artistOverride || artworkOverride || inlineMismatch?.isMismatch) && (
              <label className="flex items-center gap-2 mb-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={markVerified}
                  onChange={e => setMarkVerified(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-green-500 focus:ring-green-500"
                />
                <span className="text-xs text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-200">
                  Save as verified in Match Library
                </span>
                <ShieldCheck className="w-3.5 h-3.5 text-green-500 opacity-60" />
              </label>
            )}

            {/* Duration verification — skip if using saved match or dismissed for event */}
            {usedSuggestion && suggestionDurationRef.current ? (
              /* Using saved match with known duration — one-click confirm */
              <button
                onClick={() => handleConfirm(suggestionDurationRef.current!.verified)}
                disabled={confirmLoading}
                className="w-full flex items-center justify-center gap-2 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium text-sm"
              >
                {confirmLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> Confirm Match</>}
              </button>
            ) : durationDismissedForEvent ? (
              /* Auto-confirm without duration verification */
              <button
                onClick={() => handleConfirm(null)}
                disabled={confirmLoading}
                className="w-full flex items-center justify-center gap-2 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium text-sm"
              >
                {confirmLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> Confirm Match</>}
              </button>
            ) : (
              <div>
                {/* Duration display + edit */}
                <div className="flex items-center gap-2 mb-2">
                  <label className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">Duration:</label>
                  <input
                    type="text"
                    value={durationInput}
                    onChange={(e) => {
                      setDurationPreFilled(false);
                      const raw = e.target.value;
                      if (raw === '') { setDurationInput(''); return; }
                      // Strip everything except digits
                      const digits = raw.replace(/\D/g, '').slice(0, 5);
                      if (digits.length >= 3) {
                        // Colon always third char from right: "433" → "4:33", "1234" → "12:34"
                        const secs = digits.slice(-2);
                        const mins = digits.slice(0, -2);
                        setDurationInput(`${mins}:${secs}`);
                      } else {
                        setDurationInput(digits);
                      }
                    }}
                    onFocus={() => {
                      if (durationPreFilled) {
                        setDurationInput('');
                        setDurationPreFilled(false);
                      }
                    }}
                    placeholder="m:ss"
                    className={`input text-sm w-20 text-center py-1 ${!durationInputValid ? 'border-red-400' : ''} ${durationPreFilled ? 'bg-amber-50 dark:bg-amber-900/20' : ''}`}
                  />
                  {resolvedMatch.durationMs > 0 && (
                    <span className="text-xs text-amber-500 dark:text-amber-400">from catalog search — may differ from {resolvedMatch.platform}</span>
                  )}
                  {!resolvedMatch.durationMs && (
                    <span className="text-xs text-gray-400">enter duration from {resolvedMatch.platform} (m:ss)</span>
                  )}
                </div>
                {!durationInputValid && (
                  <p className="text-xs text-red-500 mb-2">Enter duration as m:ss (e.g. 3:17)</p>
                )}

                {/* Action buttons */}
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => handleConfirm('exact')}
                    disabled={confirmLoading || !durationInput.trim() || !durationInputValid}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium text-sm disabled:opacity-50"
                    title="Duration has been verified or corrected"
                  >
                    {confirmLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> Exact</>}
                  </button>
                  {(resolvedMatch.durationMs > 0 || (durationInput.trim() && durationInputValid && parseMmSs(durationInput))) && (
                    <button
                      onClick={() => handleConfirm('approximate')}
                      disabled={confirmLoading}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors font-medium text-sm disabled:opacity-50"
                      title="Duration shown is close enough"
                    >
                      ~Close Enough
                    </button>
                  )}
                </div>
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={handleDismissOnce}
                    disabled={confirmLoading}
                    className="flex-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 py-1 transition-colors"
                  >
                    Dismiss
                  </button>
                  <button
                    onClick={handleDismissForEvent}
                    disabled={confirmLoading}
                    className="flex-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 py-1 transition-colors"
                  >
                    Don't ask again this event
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Duration section when no URL resolved yet but request has duration */}
        {!resolvedMatch && !durationDismissedForEvent && (
          <div className="mb-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-gray-400" />
              <label className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">Duration:</label>
              <input
                type="text"
                value={durationInput}
                onChange={(e) => {
                  setDurationPreFilled(false);
                  const raw = e.target.value;
                  if (raw === '') { setDurationInput(''); return; }
                  const digits = raw.replace(/\D/g, '').slice(0, 5);
                  if (digits.length >= 3) {
                    const secs = digits.slice(-2);
                    const mins = digits.slice(0, -2);
                    setDurationInput(`${mins}:${secs}`);
                  } else {
                    setDurationInput(digits);
                  }
                }}
                onFocus={() => {
                  if (durationPreFilled) {
                    setDurationInput('');
                    setDurationPreFilled(false);
                  }
                }}
                placeholder="m:ss"
                className={`input text-sm w-20 text-center py-1 ${durationPreFilled ? 'bg-amber-50 dark:bg-amber-900/20' : ''}`}
              />
              {durationInput && (
                <span className="text-xs text-gray-400">from catalog search</span>
              )}
            </div>
            <p className="text-xs text-gray-400">Verify or update song duration before approving</p>
          </div>
        )}

        {/* Bottom actions */}
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700 flex gap-2 flex-wrap">
          <button onClick={() => { if (request) onApproveDirect(request.id); }} className="btn btn-secondary text-sm flex items-center gap-1.5" title="Approve without matching to a platform">
            <CheckCircle className="w-4 h-4" />
            Approve As-Is
          </button>
          <button onClick={() => { if (request) onDefer(request.id); }} className="btn btn-secondary text-sm flex items-center gap-1.5">
            <Clock className="w-4 h-4" />
            Research Later
          </button>
          <button onClick={() => { if (request) onReject(request.id, "Can't find on streaming platform"); }} className="btn text-danger hover:bg-danger/10 text-sm flex items-center gap-1.5">
            <X className="w-4 h-4" />
            Reject
          </button>
          {batchTotal > 1 && (
            <button onClick={onSkip} className="btn btn-secondary text-sm flex items-center gap-1.5">
              <SkipForward className="w-4 h-4" />
              Skip
            </button>
          )}
          <button onClick={onClose} className="btn btn-secondary text-sm ml-auto">Cancel</button>
        </div>
      </div>
    </div>
  );
}
